/**
 * THE-FIRST-BUILD-TELLS-THE-TRUTH-1 — what the first build page says when the
 * planner says no, and which business it says the build will land in.
 *
 * WHY-NO-BUILD-FINISHES-1 watched the planner refuse a build with "Ghost
 * cannot start a build right now: this account has no credits left. Add credits
 * or upgrade to continue." and the page show "Ghost had nothing to build from
 * that." The page read one field, and the sentence was in another.
 *
 * IT WRITES NO SENTENCE. Every word it returns is the planner's, or the one
 * sentence `creditGate` already keeps for a code.
 */
import { refusalSentence } from './creditGate'

/**
 * THE PLANNER'S OWN WORDS FOR A REFUSAL, or null when it sent none.
 *
 * Three places a refusal arrives, in the order the planner uses them: `reason`
 * is its sentence for a request it will not build, `message` carries the credit
 * gate's sentence, and `error` carries a code or a sentence depending on the
 * branch that answered.
 *
 * A CODE IS NOT A SENTENCE. `no_credits` goes through `refusalSentence`, the
 * one map from a code to Ghost's words, and a code that map does not know is
 * nothing to show: a word with no space in it is an identifier, and a merchant
 * never reads one. That is `creditGate`'s rule, kept here rather than restated.
 */
export function refusalWords(answer: unknown): string | null {
  const a = answer as { reason?: unknown; message?: unknown; error?: unknown } | null
  const said = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')
  const reason = said(a?.reason)
  if (reason !== '') return reason
  const message = said(a?.message)
  if (message !== '') return message
  const error = said(a?.error)
  const known = refusalSentence(error)
  if (known !== null) return known
  return /\s/.test(error) ? error : null
}

/** The refusal a person answers by adding credits, by its code. */
export function isNoCredits(answer: unknown): boolean {
  return (answer as { error?: unknown } | null)?.error === 'no_credits'
}

export interface BusinessOption {
  id: string
  business_name: string | null
}

/**
 * WHICH BUSINESS THE BUILD LANDS IN, AND WHETHER IT CAN BE CHANGED HERE.
 *
 * The planner builds into whichever business the person last chose. With one
 * business there is nothing to choose, so only its name shows; with two or more
 * the switcher is on the line, so a person refused for the wrong business can
 * see it and change it. No businesses at all is nothing to show.
 */
export function businessLine(
  accounts: ReadonlyArray<BusinessOption>,
  currentId: string | null,
): { name: string | null; switchable: boolean } | null {
  if (accounts.length === 0) return null
  const current = accounts.find((a) => a.id === currentId) ?? null
  return { name: current?.business_name ?? null, switchable: accounts.length > 1 }
}
